import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db, storage } from '../../services/firebase';
import { ref, deleteObject } from 'firebase/storage';
import { useAuth } from '../../contexts/AuthContext';
import { FiDownload, FiRefreshCw, FiFileText, FiSearch, FiExternalLink, FiEye, FiTrash2, FiCopy } from 'react-icons/fi';
import './AdminWholesalePage.css';

const AdminWholesalePage = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    fetchApplications();
  }, []);

  // Helper function to create PDF proxy URL
  const createPdfProxyUrl = (firebaseUrl) => {
    if (!firebaseUrl || !currentUser) return null;
    
    const functionUrl = process.env.NODE_ENV === 'production' 
      ? 'https://us-central1-greycoffee-f7f71.cloudfunctions.net/pdfProxy'
      : 'http://localhost:5001/greycoffee-f7f71/us-central1/pdfProxy';
    
    const encodedUrl = encodeURIComponent(firebaseUrl);
    return `${functionUrl}?url=${encodedUrl}`;
  };

  // Function to view PDF document
  const viewPdfDocument = async (firebaseUrl, documentName) => {
    if (!firebaseUrl || !currentUser) return;

    try {
      const token = await currentUser.getIdToken();
      const proxyUrl = createPdfProxyUrl(firebaseUrl);
      
      if (!proxyUrl) {
        console.error('Failed to create proxy URL');
        return;
      }

      // Open PDF in new tab with authentication
      const newWindow = window.open('', '_blank');
      
      // Create a form to POST the request with the auth token
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = proxyUrl;
      form.target = '_blank';
      
      // Add authorization header via a hidden iframe approach
      // Since we can't set headers on window.open, we'll use fetch to get the PDF
      fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then(response => {
        if (response.ok) {
          return response.blob();
        }
        throw new Error('Failed to fetch PDF');
      })
      .then(blob => {
        const pdfUrl = URL.createObjectURL(blob);
        if (newWindow && !newWindow.closed) {
          newWindow.location.href = pdfUrl;
        } else {
          window.open(pdfUrl, '_blank');
        }
        // Clean up the blob URL after a delay
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
      })
      .catch(error => {
        console.error('Error viewing PDF:', error);
        if (newWindow && !newWindow.closed) {
          newWindow.close();
        }
        alert(`Failed to load ${documentName}. Please try again.`);
      });
      
    } catch (error) {
      console.error('Error getting auth token:', error);
      // Try to open the URL directly as fallback
      if (firebaseUrl) {
        window.open(firebaseUrl, '_blank');
      }
    }
  };

  // Function to copy PDF link to clipboard
  const copyPdfLink = async (firebaseUrl, documentName) => {
    if (!firebaseUrl) return;
    
    try {
      await navigator.clipboard.writeText(firebaseUrl);
      alert(`${documentName} link copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy link:', error);
      alert('Failed to copy link');
    }
  };

  // Function to delete application and all associated files
  const deleteApplication = async (applicationId, documents) => {
    if (!window.confirm('Are you sure you want to delete this application? This will also delete all uploaded documents and cannot be undone.')) {
      return;
    }

    try {
      // Check if user is authenticated
      if (!currentUser) {
        alert('You must be logged in to delete applications');
        return;
      }

      // Delete the application document from Firestore first
      await deleteDoc(doc(db, 'wholesaleCustomers', applicationId));
      
      // Then try to delete files from storage (if this fails, at least the application is deleted)
      if (documents) {
        const deletePromises = [];
        
        Object.entries(documents).forEach(([docType, url]) => {
          if (url) {
            try {
              // Extract file path from Firebase Storage URL
              const urlParts = url.split('/');
              const filePathIndex = urlParts.findIndex(part => part === 'o') + 1;
              if (filePathIndex > 0 && filePathIndex < urlParts.length) {
                const filePath = decodeURIComponent(urlParts[filePathIndex].split('?')[0]);
                const fileRef = ref(storage, filePath);
                deletePromises.push(deleteObject(fileRef).catch(err => {
                  console.warn(`Failed to delete file ${filePath}:`, err);
                  return null; // Don't fail the whole operation for file deletion failures
                }));
              }
            } catch (err) {
              console.warn(`Error processing URL ${url}:`, err);
            }
          }
        });

        // Wait for all file deletions to complete (but don't fail if some fail)
        await Promise.all(deletePromises);
      }

      // Update local state
      setApplications(prevApps => prevApps.filter(app => app.id !== applicationId));
      
      alert('Application deleted successfully');
    } catch (error) {
      console.error('Error deleting application:', error);
      if (error.code === 'permission-denied') {
        alert('Permission denied. You do not have permission to delete this application.');
      } else if (error.code === 'not-found') {
        alert('Application not found. It may have already been deleted.');
        // Remove from local state anyway
        setApplications(prevApps => prevApps.filter(app => app.id !== applicationId));
      } else {
        alert('Failed to delete application: ' + (error.message || 'Unknown error'));
      }
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'wholesaleCustomers'));
      const applicationsList = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt) || new Date()
      }));
      
      // Sort by creation date (newest first)
      applicationsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setApplications(applicationsList);
    } catch (err) {
      console.error('Failed to fetch applications:', err);
    }
    setLoading(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchApplications();
    setRefreshing(false);
  };

  const exportApplications = () => {
    const csvHeaders = [
      'Business Name',
      'Street Address',
      'Street Address Line 2',
      'City',
      'Commercial Registration',
      'Establishment ID',
      'PO Box',
      'Purchase Phone',
      'Purchase Email',
      'Business Owner QID',
      'Business Owner Name',
      'Applicant QID',
      'Applicant Name',
      'Credit Limit',
      'Credit Period',
      'Payment Mode',
      'Business Type',
      'CR Document',
      'Owner QID Document',
      'Computer Card Document',
      'Guarantee Cheque Document',
      'Terms Accepted',
      'Privacy Policy Accepted',
      'Application Date',
      'Status'
    ];

    const csvData = filteredApplications.map(app => [
      app.businessName || '',
      app.streetAddress || '',
      app.streetAddressLine2 || '',
      app.city || '',
      app.commercialRegistration || '',
      app.establishmentId || '',
      app.poBox || '',
      app.purchasePhone || '',
      app.purchaseEmail || '',
      app.businessOwnerQIDNumber || '',
      app.businessOwnerName || '',
      app.applicantQIDNumber || '',
      app.applicantName || '',
      app.creditLimit || '',
      app.creditPeriod || '',
      app.paymentMode || '',
      app.businessType || '',
      app.documents?.crDocument ? 'Uploaded' : 'Not Uploaded',
      app.documents?.ownerQID ? 'Uploaded' : 'Not Uploaded',
      app.documents?.computerCard ? 'Uploaded' : 'Not Uploaded',
      app.documents?.guaranteeCheque ? 'Uploaded' : 'Not Uploaded',
      app.termsAndConditionsAccepted ? 'Yes' : 'No',
      app.privacyPolicyAccepted ? 'Yes' : 'No',
      formatDate(app.createdAt),
      app.status || 'N/A'
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `wholesale-applications-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredApplications = applications.filter(app =>
    searchTerm === '' || 
    app.businessName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.purchaseEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.businessType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="wholesale-applications">
        <div className="loading-container">
          <FiRefreshCw className="spin" size={24} />
          <span>Loading wholesale applications...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="wholesale-applications">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Wholesale Applications</h1>
            <p>View and export wholesale customer applications ({applications.length} total)</p>
          </div>
          <div className="header-actions">
            <button 
              className="btn btn-secondary"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <FiRefreshCw size={16} className={refreshing ? 'spin' : ''} />
              Refresh
            </button>
            <button 
              className="btn btn-primary"
              onClick={exportApplications}
              disabled={filteredApplications.length === 0}
            >
              <FiDownload size={16} />
              Export CSV ({filteredApplications.length})
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-container">
          <FiSearch size={20} />
          <input
            type="text"
            placeholder="Search applications by business name, contact, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {/* Applications Table */}
      <div className="applications-container">
        {filteredApplications.length === 0 ? (
          <div className="empty-state">
            <FiFileText size={48} />
            <h3>No applications found</h3>
            <p>No wholesale applications match your search criteria.</p>
          </div>
        ) : (
          <div className="table-container">
            <div className="applications-grid">
              {filteredApplications.map((app) => (
                <div key={app.id} className="application-card">
                  <div className="card-header">
                    <h3 className="business-name">{app.businessName || 'N/A'}</h3>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteApplication(app.id, app.documents)}
                      title="Delete Application"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                  
                  <div className="card-content">
                    <div className="info-section">
                      <h4>Business Information</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <label>Address:</label>
                          <span>{app.streetAddress || 'N/A'}{app.streetAddressLine2 ? `, ${app.streetAddressLine2}` : ''}</span>
                        </div>
                        <div className="info-item">
                          <label>City:</label>
                          <span>{app.city || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Business Type:</label>
                          <span>{app.businessType || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>CR #:</label>
                          <span>{app.commercialRegistration || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Establishment ID:</label>
                          <span>{app.establishmentId || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>PO Box:</label>
                          <span>{app.poBox || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <h4>Contact Information</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <label>Purchase Phone:</label>
                          <span>{app.purchasePhone || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Purchase Email:</label>
                          <span>{app.purchaseEmail || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Business Owner:</label>
                          <span>{app.businessOwnerName || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Owner QID:</label>
                          <span>{app.businessOwnerQIDNumber || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Applicant:</label>
                          <span>{app.applicantName || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Applicant QID:</label>
                          <span>{app.applicantQIDNumber || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <h4>Financial Terms</h4>
                      <div className="info-grid">
                        <div className="info-item">
                          <label>Credit Limit:</label>
                          <span>{app.creditLimit || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Credit Period:</label>
                          <span>{app.creditPeriod || 'N/A'}</span>
                        </div>
                        <div className="info-item">
                          <label>Payment Mode:</label>
                          <span>{app.paymentMode || 'N/A'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <h4>Documents & Legal</h4>
                      <div className="documents-status">
                        <div className={`doc-status ${app.documents?.crDocument ? 'uploaded' : 'missing'}`}>
                          <span>CR Document:</span>
                          <div className="doc-actions">
                            <span className="status-icon">{app.documents?.crDocument ? '✓' : '✗'}</span>
                            {app.documents?.crDocument && (
                              <>
                                <button 
                                  className="view-doc-btn"
                                  onClick={() => viewPdfDocument(app.documents.crDocument, 'CR Document')}
                                  title="View CR Document"
                                >
                                  <FiEye size={14} />
                                </button>
                                <button 
                                  className="copy-link-btn"
                                  onClick={() => copyPdfLink(app.documents.crDocument, 'CR Document')}
                                  title="Copy CR Document Link"
                                >
                                  <FiCopy size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className={`doc-status ${app.documents?.ownerQID ? 'uploaded' : 'missing'}`}>
                          <span>Owner QID:</span>
                          <div className="doc-actions">
                            <span className="status-icon">{app.documents?.ownerQID ? '✓' : '✗'}</span>
                            {app.documents?.ownerQID && (
                              <>
                                <button 
                                  className="view-doc-btn"
                                  onClick={() => viewPdfDocument(app.documents.ownerQID, 'Owner QID')}
                                  title="View Owner QID"
                                >
                                  <FiEye size={14} />
                                </button>
                                <button 
                                  className="copy-link-btn"
                                  onClick={() => copyPdfLink(app.documents.ownerQID, 'Owner QID')}
                                  title="Copy Owner QID Link"
                                >
                                  <FiCopy size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className={`doc-status ${app.documents?.computerCard ? 'uploaded' : 'missing'}`}>
                          <span>Computer Card:</span>
                          <div className="doc-actions">
                            <span className="status-icon">{app.documents?.computerCard ? '✓' : '✗'}</span>
                            {app.documents?.computerCard && (
                              <>
                                <button 
                                  className="view-doc-btn"
                                  onClick={() => viewPdfDocument(app.documents.computerCard, 'Computer Card')}
                                  title="View Computer Card"
                                >
                                  <FiEye size={14} />
                                </button>
                                <button 
                                  className="copy-link-btn"
                                  onClick={() => copyPdfLink(app.documents.computerCard, 'Computer Card')}
                                  title="Copy Computer Card Link"
                                >
                                  <FiCopy size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className={`doc-status ${app.documents?.guaranteeCheque ? 'uploaded' : 'missing'}`}>
                          <span>Guarantee Cheque:</span>
                          <div className="doc-actions">
                            <span className="status-icon">{app.documents?.guaranteeCheque ? '✓' : '✗'}</span>
                            {app.documents?.guaranteeCheque && (
                              <>
                                <button 
                                  className="view-doc-btn"
                                  onClick={() => viewPdfDocument(app.documents.guaranteeCheque, 'Guarantee Cheque')}
                                  title="View Guarantee Cheque"
                                >
                                  <FiEye size={14} />
                                </button>
                                <button 
                                  className="copy-link-btn"
                                  onClick={() => copyPdfLink(app.documents.guaranteeCheque, 'Guarantee Cheque')}
                                  title="Copy Guarantee Cheque Link"
                                >
                                  <FiCopy size={14} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="legal-status">
                        <div className={`agreement-status ${app.termsAndConditionsAccepted ? 'accepted' : 'not-accepted'}`}>
                          <span>Terms & Conditions:</span>
                          <span className="status-icon">{app.termsAndConditionsAccepted ? '✓' : '✗'}</span>
                        </div>
                        <div className={`agreement-status ${app.privacyPolicyAccepted ? 'accepted' : 'not-accepted'}`}>
                          <span>Privacy Policy:</span>
                          <span className="status-icon">{app.privacyPolicyAccepted ? '✓' : '✗'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer">
                      <span className="application-date">Applied: {formatDate(app.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminWholesalePage;